import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi } from "../lib/api";
import { IS_LOCAL_EDITION } from "../lib/edition";
import { trackMarketingEvent } from "../lib/marketingAnalytics";

const AuthCtx = createContext(null);

const U_KEY = "dteflow_user";
const LEGACY_TOKEN_KEYS = [
  "dteflow_access_token",
  "dteflow_refresh_token",
  "dtelab_access_token",
  "dtelab_refresh_token",
];
const LOCAL_USER = {
  id: "local",
  email: "local@dteflow",
  name: "DTE Flow Local",
  role: "administrador",
};

function clearLegacyTokenStorage() {
  try {
    LEGACY_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage puede no estar disponible en algunos modos privados.
  }
}

function migrateLegacyUserKeys() {
  try {
    const legacyUser = localStorage.getItem("dtelab_user");
    if (legacyUser != null && localStorage.getItem(U_KEY) == null) {
      localStorage.setItem(U_KEY, legacyUser);
    }
    const legacyAmbiente = localStorage.getItem("dtelab_ambiente_chosen");
    if (legacyAmbiente != null && localStorage.getItem("dteflow_ambiente_chosen") == null) {
      localStorage.setItem("dteflow_ambiente_chosen", legacyAmbiente);
    }
    localStorage.removeItem("dtelab_user");
    localStorage.removeItem("dtelab_ambiente_chosen");
  } catch {
    // noop
  }
  clearLegacyTokenStorage();
}

migrateLegacyUserKeys();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    if (IS_LOCAL_EDITION) return LOCAL_USER;
    try {
      const u = localStorage.getItem(U_KEY);
      return u ? JSON.parse(u) : null;
    } catch {
      localStorage.removeItem(U_KEY);
      return null;
    }
  });
  const [loading, setLoading] = useState(!IS_LOCAL_EDITION);

  useEffect(() => {
    if (IS_LOCAL_EDITION) {
      setUser(LOCAL_USER);
      setLoading(false);
      return;
    }
    const bootstrap = async () => {
      try {
        clearLegacyTokenStorage();
        let nextUser;
        try {
          nextUser = await authApi.me();
        } catch (meErr) {
          if (meErr?.response?.status !== 401) throw meErr;
          await authApi.refresh();
          nextUser = await authApi.me();
        }
        setUser(nextUser);
        localStorage.setItem(U_KEY, JSON.stringify(nextUser));
      } catch {
        clearLegacyTokenStorage();
        localStorage.removeItem(U_KEY);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  const login = useCallback(async (email, password) => {
    if (IS_LOCAL_EDITION) {
      setUser(LOCAL_USER);
      return LOCAL_USER;
    }
    const r = await authApi.login(email, password);
    clearLegacyTokenStorage();
    localStorage.setItem(U_KEY, JSON.stringify(r.user));
    setUser(r.user);
    return r.user;
  }, []);

  const loginWithGoogleCode = useCallback(async (code) => {
    if (IS_LOCAL_EDITION) {
      setUser(LOCAL_USER);
      return { user: LOCAL_USER, isNewUser: false };
    }
    const r = await authApi.googleExchange(code);
    clearLegacyTokenStorage();
    localStorage.setItem(U_KEY, JSON.stringify(r.user));
    setUser(r.user);
    if (r?.isNewUser) {
      trackMarketingEvent("sign_up", { placement: "register_google", product: "fiscal", destination: "web" });
    }
    return r;
  }, []);

  const register = useCallback(async (email, password, name) => {
    if (IS_LOCAL_EDITION) {
      setUser(LOCAL_USER);
      return LOCAL_USER;
    }
    const r = await authApi.register(email, password, name);
    clearLegacyTokenStorage();
    localStorage.setItem(U_KEY, JSON.stringify(r.user));
    setUser(r.user);
    trackMarketingEvent("sign_up", { placement: "register_api", product: "fiscal", destination: "web" });
    return r.user;
  }, []);

  const completeOnboarding = useCallback(async () => {
    if (IS_LOCAL_EDITION) {
      setUser(LOCAL_USER);
      return LOCAL_USER;
    }
    await authApi.completeOnboarding();
    const next = { ...(user || {}), onboarding_completed: true };
    localStorage.setItem(U_KEY, JSON.stringify(next));
    setUser(next);
    return next;
  }, [user]);

  const logout = useCallback(async () => {
    if (IS_LOCAL_EDITION) {
      setUser(LOCAL_USER);
      return;
    }
    try {
      await authApi.logout();
    } catch {
      // La limpieza local no debe depender de la red.
    }
    clearLegacyTokenStorage();
    localStorage.removeItem(U_KEY);
    localStorage.removeItem("dteflow_ambiente_chosen");
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, loginWithGoogleCode, register, logout, completeOnboarding }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
